import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// arsenal-tpms/frontend/src/components/schematics/VehicleRouteMapModal.jsx
// ✅ VERSIÓN OPTIMIZADA - Mapa estático en hover, popups limpios, sincronización bidireccional
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Alert,
  useTheme,
  CircularProgress,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  MyLocation as CenterIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { GoogleMap, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import { api } from '../../services/api';

const VehicleRouteMapModal = ({
  vehicle,
  dateRange,
  onLoaded,
  hoveredTimestamp = null,
  formatUserDateTimeSync,
  effectiveTimezone,
  isVisible = false,
  onMapPointClick = null // 🆕 Callback para cuando se hace click en un punto del mapa
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Estados principales
  const [gpsData, setGpsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showRoute, setShowRoute] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showStats, setShowStats] = useState(true);

  // Estados del mapa
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState({ lat: 19.4326, lng: -99.1332 });

  // Estado para el punto hover
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Refs para evitar recargas infinitas
  const loadedDataRef = useRef(null);
  const isLoadingRef = useRef(false);
  const shouldFitBoundsRef = useRef(false);

  // Configuración del mapa
  const mapContainerStyle = {
    width: '100%',
    height: '100%',
    minHeight: '400px'
  };

  // Cargar datos GPS
  const loadGPSData = useCallback(async () => {
    if (isLoadingRef.current) {
      console.log('🗺️ Already loading GPS data, skipping...');
      return;
    }

    if (!vehicle?.id) {
      console.log(t('hardcoded.components_schematics_vehicleroutemapmodal.no_vehicle_id_skipping_gps'));
      return;
    }

    if (!dateRange?.startDate || !dateRange?.endDate) {
      console.log(t('hardcoded.components_schematics_vehicleroutemapmodal.no_date_range_skipping_gps'));
      return;
    }

    const dataKey = `${vehicle.id}_${dateRange.startDate}_${dateRange.endDate}`;
    if (loadedDataRef.current === dataKey && gpsData.length > 0) {
      console.log('🗺️ GPS data already loaded for this range, skipping');
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      const startDateTime = dateRange.startDate + 'T00:00:00.000Z';
      const endDateTime = dateRange.endDate + 'T23:59:59.999Z';

      console.log('🗺️ Loading GPS data:', {
        vehicleId: vehicle.id,
        startDate: startDateTime,
        endDate: endDateTime
      });

      const response = await api.telemetry.getVehicleGPS(vehicle.id, {
        start_date: startDateTime,
        end_date: endDateTime,
        limit: 5000
      });

      console.log('📍 GPS data received:', {
        total: response.data?.gps_data?.length || 0
      });

      if (response.data?.gps_data && response.data.gps_data.length > 0) {
        const validGpsPoints = response.data.gps_data.filter(
          point => point.latitude != null && point.longitude != null
        );

        setGpsData(validGpsPoints);
        loadedDataRef.current = dataKey;
        shouldFitBoundsRef.current = true;

        if (validGpsPoints.length > 0) {
          const firstPoint = validGpsPoints[0];
          setCenter({
            lat: parseFloat(firstPoint.latitude),
            lng: parseFloat(firstPoint.longitude)
          });
        }

        console.log(`✅ ${validGpsPoints.length} valid GPS points loaded`);

        if (onLoaded) {
          onLoaded(validGpsPoints);
        }
      } else {
        setGpsData([]);
        setError(t('hardcoded.components_schematics_vehicleroutemapmodal.no_se_encontraron_datos_gps'));
      }
    } catch (err) {
      console.error('❌ Error loading GPS data:', err);
      setError(err.response?.data?.detail || t('hardcoded.components_schematics_vehicleroutemapmodal.error_al_cargar_datos_gps'));
      setGpsData([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [vehicle?.id, dateRange?.startDate, dateRange?.endDate, onLoaded, gpsData.length]);

  // Cargar datos cuando cambia el rango
  useEffect(() => {
    if (isVisible && vehicle?.id && dateRange?.startDate && dateRange?.endDate) {
      const dataKey = `${vehicle.id}_${dateRange.startDate}_${dateRange.endDate}`;

      if (loadedDataRef.current !== dataKey) {
        console.log('🔄 Date range changed, loading GPS');
        loadGPSData();
      }
    } else if (!isVisible) {
      console.log(t('hardcoded.components_schematics_vehicleroutemapmodal.map_not_visible'));
    }
  }, [isVisible, vehicle?.id, dateRange?.startDate, dateRange?.endDate]);

  // 🆕 Sincronizar hover con tolerancia - SIN MOVER EL MAPA
  useEffect(() => {
    if (!hoveredTimestamp || !gpsData || gpsData.length === 0) {
      setHoveredPoint(null);
      return;
    }

    const targetTime = new Date(hoveredTimestamp).getTime();

    let closestPoint = null;
    let minDiff = Infinity;

    gpsData.forEach(point => {
      const pointTime = new Date(point.timestamp).getTime();
      const diff = Math.abs(pointTime - targetTime);

      if (diff < minDiff) {
        minDiff = diff;
        closestPoint = point;
      }
    });

    if (closestPoint && minDiff < 15 * 60 * 1000) {
      console.log('🎯 Hover point found:', {
        targetTime: new Date(targetTime).toISOString(),
        pointTime: closestPoint.timestamp,
        diffSeconds: Math.floor(minDiff / 1000)
      });

      setHoveredPoint(closestPoint);
      // 🚫 REMOVIDO: map.panTo() - El mapa ya NO se mueve
    } else {
      setHoveredPoint(null);
    }
  }, [hoveredTimestamp, gpsData]); // 🆕 Removido 'map' de las dependencias

  // Procesar ruta para polyline
  const routePath = useMemo(() => {
    return gpsData.map(point => ({
      lat: parseFloat(point.latitude),
      lng: parseFloat(point.longitude)
    }));
  }, [gpsData]);

  // Obtener primer y último punto
  const startPoint = useMemo(() => gpsData[0], [gpsData]);
  const endPoint = useMemo(() => gpsData[gpsData.length - 1], [gpsData]);

  // Mostrar TODOS los puntos GPS intermedios
  const intermediatePoints = useMemo(() => {
    if (gpsData.length <= 2) return [];

    const points = gpsData.slice(1, -1);

    console.log(t('hardcoded.components_schematics_vehicleroutemapmodal.showing_all_length_gps_points', { length: points.length, length: gpsData.length }));
    return points;
  }, [gpsData]);

  // Formatear valores
  const formatValue = (value, decimals = 2) => {
    if (value == null || value === undefined) return t('hardcoded.components_schematics_vehicleroutemapmodal.no_disponible');
    const num = parseFloat(value);
    if (isNaN(num)) return t('hardcoded.components_schematics_vehicleroutemapmodal.no_disponible');
    return num.toFixed(decimals);
  };

  // Formatear timestamp COMPACTO
  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return i18n.t('hardcoded.components_schematics_vehicleroutemapmodal.no_disponible');
    try {
      const date = new Date(timestamp);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}/${month} ${hours}:${minutes}`;
    } catch (error) {
      console.error(t('hardcoded.components_schematics_vehicleroutemapmodal.error_formatting_timestamp'), error);
      return i18n.t('hardcoded.components_schematics_vehicleroutemapmodal.no_disponible');
    }
  }, []);

  // Calcular estadísticas
  const routeStats = useMemo(() => {
    if (gpsData.length === 0) return null;

    const speeds = gpsData
      .map(p => parseFloat(p.speed))
      .filter(s => !isNaN(s) && s > 0);

    const avgSpeed = speeds.length > 0
      ? (speeds.reduce((a, b) => a + b, 0) / speeds.length).toFixed(1)
      : '0.0';

    const maxSpeed = speeds.length > 0
      ? Math.max(...speeds).toFixed(1)
      : '0.0';

    return {
      avgSpeed,
      maxSpeed
    };
  }, [gpsData]);

  // Centrar mapa en todos los puntos
  const fitBounds = useCallback(() => {
    if (map && gpsData.length > 0 && window.google?.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      gpsData.forEach(point => {
        const lat = parseFloat(point.latitude);
        const lng = parseFloat(point.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          bounds.extend({ lat, lng });
        }
      });
      map.fitBounds(bounds);
    }
  }, [map, gpsData]);

  // FitBounds automático cuando se cargan datos
  useEffect(() => {
    if (map && gpsData.length > 0 && shouldFitBoundsRef.current) {
      console.log('🎯 Auto-fitting bounds to show all route');
      fitBounds();
      shouldFitBoundsRef.current = false;
    }
  }, [map, gpsData, fitBounds]);

  // Handler para cuando el mapa se carga
  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    setMapLoaded(true);
    console.log('✅ Google Maps loaded');
  }, []);

  // Manual refresh
  const handleManualRefresh = useCallback(() => {
    loadedDataRef.current = null;
    loadGPSData();
  }, [loadGPSData]);

  // 🆕 Handler para click en puntos del mapa
  const handlePointClick = useCallback((point) => {
    setSelectedPoint(point);

    // Notificar al padre para que muestre el tooltip en la gráfica
    if (onMapPointClick) {
      onMapPointClick(point.timestamp);
    }
  }, [onMapPointClick]);

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('hardcoded.components_schematics_vehicleroutemapmodal.cargando_datos_gps_del_vehiculo')}</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="warning"
        sx={{ m: 2 }}
        action={
          <IconButton color="inherit" size="small" onClick={handleManualRefresh}>
            <RefreshIcon />
          </IconButton>
        }
      >
        {String(error)}
      </Alert>
    );
  }

  if (gpsData.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t('hardcoded.components_schematics_vehicleroutemapmodal.no_hay_datos_gps_disponibles')}</Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Estadísticas */}
      {routeStats && (
        <Paper sx={{ mb: 1 }}>
          <Box
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            onClick={() => setShowStats(!showStats)}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              {t('hardcoded.components_schematics_vehicleroutemapmodal.estadisticas_de_ruta')}</Typography>
            <IconButton size="small">
              {showStats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={showStats}>
            <Box sx={{ p: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('hardcoded.components_schematics_vehicleroutemapmodal.vel_promedio')}</Typography>
                    <Typography variant="body2" color="success.main" fontWeight="bold">
                      {String(routeStats.avgSpeed)} km/h
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('hardcoded.components_schematics_vehicleroutemapmodal.vel_maxima')}</Typography>
                    <Typography variant="body2" color="warning.main" fontWeight="bold">
                      {String(routeStats.maxSpeed)} km/h
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Mapa de Google */}
      <Paper sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={12}
          options={{
            mapTypeId: 'roadmap',
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            mapTypeControl: true
          }}
          onLoad={onMapLoad}
        >
          {/* Polyline de la ruta */}
          {showRoute && routePath.length > 1 && mapLoaded && window.google?.maps && (
            <Polyline
              path={routePath}
              options={{
                strokeColor: theme.palette.primary.main,
                strokeOpacity: 0.8,
                strokeWeight: 4,
                geodesic: true
              }}
            />
          )}

          {/* Marcador de inicio */}
          {startPoint && mapLoaded && window.google?.maps && (
            <Marker
              position={{
                lat: parseFloat(startPoint.latitude),
                lng: parseFloat(startPoint.longitude)
              }}
              title={t('hardcoded.components_schematics_vehicleroutemapmodal.inicio_del_recorrido')}
              onClick={() => handlePointClick(startPoint)}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
                    <circle cx="14" cy="14" r="12" fill="#4caf50" stroke="white" stroke-width="3"/>
                    <text x="14" y="18" text-anchor="middle" fill="white" font-size="12" font-weight="bold">S</text>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(28, 28),
                anchor: new window.google.maps.Point(14, 14)
              }}
              zIndex={100}
            />
          )}

          {/* Puntos GPS intermedios */}
          {intermediatePoints.map((point, index) => (
            <Marker
              key={`gps-${index}`}
              position={{
                lat: parseFloat(point.latitude),
                lng: parseFloat(point.longitude)
              }}
              title={`${formatTimestamp(point.timestamp)}`}
              onClick={() => handlePointClick(point)}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6">
                    <circle cx="3" cy="3" r="2" fill="${theme.palette.primary.main}" stroke="white" stroke-width="0.5"/>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(6, 6),
                anchor: new window.google.maps.Point(3, 3)
              }}
              zIndex={50}
            />
          ))}

          {/* Marcador de fin */}
          {endPoint && endPoint !== startPoint && mapLoaded && window.google?.maps && (
            <Marker
              position={{
                lat: parseFloat(endPoint.latitude),
                lng: parseFloat(endPoint.longitude)
              }}
              title={t('hardcoded.components_schematics_vehicleroutemapmodal.ultima_posicion_conocida')}
              onClick={() => handlePointClick(endPoint)}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
                    <circle cx="15" cy="15" r="13" fill="#f44336" stroke="white" stroke-width="3"/>
                    <text x="15" y="20" text-anchor="middle" fill="white" font-size="13" font-weight="bold">E</text>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(30, 30),
                anchor: new window.google.maps.Point(15, 15)
              }}
              zIndex={100}
            />
          )}

          {/* Marcador de punto hover - PIN DE UBICACIÓN */}
          {hoveredPoint && mapLoaded && window.google?.maps && (
            <Marker
              position={{
                lat: parseFloat(hoveredPoint.latitude),
                lng: parseFloat(hoveredPoint.longitude)
              }}
              title={t('hardcoded.components_schematics_vehicleroutemapmodal.punto_seleccionado_en_grafica')}
              icon={{
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
                    <path d="M16 0C7.163 0 0 7.163 0 16c0 13 16 26 16 26s16-13 16-26c0-8.837-7.163-16-16-16z" 
                      fill="#ff9800" stroke="white" stroke-width="2"/>
                    <circle cx="16" cy="16" r="6" fill="white"/>
                  </svg>
                `),
                scaledSize: new window.google.maps.Size(32, 42),
                anchor: new window.google.maps.Point(16, 42)
              }}
              zIndex={1000}
              animation={window.google.maps.Animation.BOUNCE}
            />
          )}

          {/* 🆕 InfoWindow MEJORADO para hover - Más compacto y limpio */}
          {hoveredPoint && !selectedPoint && mapLoaded && window.google?.maps && (
            <InfoWindow
              position={{
                lat: parseFloat(hoveredPoint.latitude),
                lng: parseFloat(hoveredPoint.longitude)
              }}
              options={{
                pixelOffset: new window.google.maps.Size(0, -45)
              }}
              onCloseClick={() => setHoveredPoint(null)}
            >
              <Box sx={{ p: 0.5, minWidth: 120, maxWidth: 160 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 0.4,
                    borderBottom: '1px solid #ff9800'
                  }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem', color: '#ff9800' }}>
                      🕐 {formatTimestamp(hoveredPoint.timestamp)}
                    </Typography>
                  </Box>

                  {hoveredPoint.speed != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {t('hardcoded.components_schematics_vehicleroutemapmodal.velocidad')}</Typography>
                      <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem', color: 'success.main' }}>
                        {formatValue(hoveredPoint.speed, 1)} km/h
                      </Typography>
                    </Box>
                  )}

                  {hoveredPoint.altitude != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {t("hardcoded.components_schematics_vehicleroutemapmodal.altitud")}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {formatValue(hoveredPoint.altitude, 1)} m
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </InfoWindow>
          )}

          {/* 🆕 InfoWindow MEJORADO para puntos clickeados - Más compacto */}
          {selectedPoint && mapLoaded && window.google?.maps && (
            <InfoWindow
              position={{
                lat: parseFloat(selectedPoint.latitude),
                lng: parseFloat(selectedPoint.longitude)
              }}
              onCloseClick={() => setSelectedPoint(null)}
            >
              <Box sx={{ p: 0.5, minWidth: 120, maxWidth: 160 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 0.4,
                    borderBottom: '1px solid',
                    borderColor: 'primary.main'
                  }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem', color: 'primary.main' }}>
                      🕐 {formatTimestamp(selectedPoint.timestamp)}
                    </Typography>
                  </Box>

                  {selectedPoint.speed != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {t('hardcoded.components_schematics_vehicleroutemapmodal.velocidad')}</Typography>
                      <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.75rem', color: 'success.main' }}>
                        {formatValue(selectedPoint.speed, 1)} km/h
                      </Typography>
                    </Box>
                  )}

                  {selectedPoint.altitude != null && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        {t("hardcoded.components_schematics_vehicleroutemapmodal.altitud")}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {formatValue(selectedPoint.altitude, 1)} m
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* Controles flotantes */}
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            zIndex: 10
          }}
        >
          <Tooltip title={t('hardcoded.components_schematics_vehicleroutemapmodal.reset_zoom')} placement="left">
            <IconButton
              size="small"
              sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}
              onClick={fitBounds}
            >
              <CenterIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('hardcoded.components_schematics_vehicleroutemapmodal.actualizar_datos')} placement="left">
            <IconButton
              size="small"
              sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}
              onClick={handleManualRefresh}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};

export default VehicleRouteMapModal;